import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Loader2Icon } from 'lucide-react';
import { format } from 'date-fns';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useCustomer } from '@/lib/hooks/useCustomers';
import { customerApi } from '@/lib/api/customers';
import { AssignJobDialog } from '@/components/customers/AssignJobDialog';
import { CustomerSummaryStats } from '@/components/customers/CustomerSummaryStats';
import { CustomerHeader } from '@/components/customers/CustomerHeader';
import { CustomerOverviewTab } from '@/components/customers/CustomerOverviewTab';
import { CustomerProfessionalTab } from '@/components/customers/CustomerProfessionalTab';
import { CustomerEducationTab } from '@/components/customers/CustomerEducationTab';
import { CustomerBillingTab } from '@/components/customers/CustomerBillingTab';
import { CustomerAccountTab } from '@/components/customers/CustomerAccountTab';

export const Route = createFileRoute('/admin/customers/$id')({
  component: CustomerDetailsPage,
});

const fmt = (v?: string | Date | null) => (v ? format(new Date(v), 'MMM dd, yyyy') : null);

const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'professional', label: 'Professional' },
  { value: 'education', label: 'Education & Skills' },
  { value: 'billing', label: 'Billing' },
  { value: 'activity', label: 'Activity' },
];

function CustomerDetailsPage() {
  const { id } = Route.useParams();
  const { data: customer, isLoading, error } = useCustomer(id);
  const [assignJobDialogOpen, setAssignJobDialogOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: assignedJobs = [], isLoading: isLoadingAssignedJobs } = useQuery({
    queryKey: ['customer', id, 'assigned-jobs'],
    queryFn: () => customerApi.getAssignedJobs(id),
    enabled: !!customer,
  });

  const formattedDates = useMemo(() => {
    if (!customer) return null;
    return {
      created: fmt(customer.createdAt),
      updated: fmt(customer.updatedAt),
      lastProfileUpdate: fmt(customer.lastProfileUpdate),
      verification: fmt(customer.verificationDate),
    };
  }, [customer]);

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2Icon className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold">Customer Not Found</h2>
          <p className="mt-2 text-sm text-muted-foreground">The customer with ID {id} does not exist.</p>
          <Link to="/admin/customers">
            <Button className="mt-4">Back to Customers</Button>
          </Link>
        </div>
      </div>
    );
  }

  const initials = customer.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="mx-auto w-full max-w-7xl space-y-6 p-4 pb-16 sm:p-6 lg:p-8">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admin/customers">Customers</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{customer.name || 'Customer Details'}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <CustomerHeader
        customer={customer}
        initials={initials}
        joined={formattedDates?.created ?? null}
        onAssignJobClick={() => setAssignJobDialogOpen(true)}
      />

      <CustomerSummaryStats customer={customer} />

      <AssignJobDialog
        open={assignJobDialogOpen}
        onOpenChange={setAssignJobDialogOpen}
        customerId={customer.id}
        customerName={customer.name}
        onAssign={async (jobId, reason) => {
          await customerApi.assignJob(customer.id, jobId, reason);
          queryClient.invalidateQueries({ queryKey: ['customer', customer.id, 'assigned-jobs'] });
        }}
      />

      <Tabs defaultValue="overview" className="space-y-5">
        <div className="overflow-x-auto">
          <TabsList className="w-full justify-start sm:w-auto">
            {TABS.map((t) => (
              <TabsTrigger key={t.value} value={t.value} className="whitespace-nowrap">
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <TabsContent value="overview">
          <CustomerOverviewTab customer={customer} />
        </TabsContent>
        <TabsContent value="professional">
          <CustomerProfessionalTab customer={customer} />
        </TabsContent>
        <TabsContent value="education">
          <CustomerEducationTab customer={customer} />
        </TabsContent>
        <TabsContent value="billing">
          <CustomerBillingTab customer={customer} />
        </TabsContent>
        <TabsContent value="activity">
          <CustomerAccountTab
            customer={customer}
            assignedJobs={assignedJobs}
            isLoadingJobs={isLoadingAssignedJobs}
            onAssignJobClick={() => setAssignJobDialogOpen(true)}
            formattedDates={{
              created: formattedDates?.created ?? null,
              updated: formattedDates?.updated ?? null,
              lastProfileUpdate: formattedDates?.lastProfileUpdate ?? null,
              verification: formattedDates?.verification ?? null,
            }}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
